import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getEducationalPosts } from "../../api";
import SiteSearchBar from "../../components/common/SiteSearch/SiteSearchBar";
import useGoToPricing from "../../hooks/useGoToPricing";
import { isLoggedIn } from "../../utils/isLoggedIn";
import { scrollPageToTop } from "../../utils/scrollPageToTop";
import Header from "../Homepage/sections/Header";
import Footer from "../Homepage/sections/Footer";
import { HomepageGlobalStyles } from "../Homepage/styles";
import MembersPagination from "../Members/MembersPagination";
import SearchFeedSkeleton from "./SearchFeedSkeleton";
import SearchResultCard from "./SearchResultCard";
import {
  buildSearchFilter,
  buildSearchUrl,
  getSearchTermFromLocation,
  SEARCH_PAGE_SIZE,
} from "./searchUtils";
import {
  SearchFeedInner,
  SearchFeedSection,
  SearchHero,
  SearchHeroBg,
  SearchHeroFormWrap,
  SearchHeroInner,
  SearchHeroSubtitle,
  SearchHeroTitle,
  SearchPage,
  SearchPaginationWrap,
  SearchResultList,
  SearchResultsSummary,
  SearchStateCard,
  SearchStateText,
  SearchStateTitle,
} from "./Search.styles";
import "./Search.feed.scss";

const Search = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const goToPricing = useGoToPricing();

  const term = getSearchTermFromLocation(location);
  const hasQuery = Boolean(term);
  const loggedIn = isLoggedIn();

  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [loading, setLoading] = useState(false);

  const totalPages = Math.max(1, Math.ceil(total / SEARCH_PAGE_SIZE));

  useEffect(() => {
    document.title = term
      ? `Search: ${term} | bitcoin.live`
      : "Search | bitcoin.live";
  }, [term]);

  useEffect(() => {
    const legacyTerm = location.state?.term;
    if (!term && legacyTerm) {
      navigate(buildSearchUrl(legacyTerm), { replace: true });
    }
  }, [location.state?.term, navigate, term]);

  const fetchResults = useCallback(
    async (pageIndex = 0) => {
      if (!term) {
        setPosts([]);
        setTotal(0);
        setActivePage(1);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const filter = buildSearchFilter(term);
        const response = await getEducationalPosts(
          filter,
          SEARCH_PAGE_SIZE,
          pageIndex
        );
        setPosts(response?.data?.items || []);
        setTotal(response?.data?.total || 0);
        setActivePage(pageIndex + 1);
      } finally {
        setLoading(false);
      }
    },
    [term]
  );

  useEffect(() => {
    fetchResults(0);
  }, [fetchResults]);

  const handlePageChange = (pageNumber) => {
    fetchResults(pageNumber - 1);
    scrollPageToTop();
  };

  const resultsSummary = useMemo(() => {
    if (!hasQuery || loading) return null;
    return (
      <SearchResultsSummary>
        {total} result{total === 1 ? "" : "s"} for{" "}
        <strong>&ldquo;{term}&rdquo;</strong>
      </SearchResultsSummary>
    );
  }, [hasQuery, loading, term, total]);

  return (
    <SearchPage className="search-page">
      <HomepageGlobalStyles />
      <Header onSubscribeClick={goToPricing} hideSpacer />

      <SearchHero>
        <SearchHeroBg aria-hidden="true" />
        <SearchHeroInner>
          {hasQuery ? (
            <SearchHeroTitle title={term}>
              <span>{term}</span>
            </SearchHeroTitle>
          ) : (
            <>
              <SearchHeroTitle>
                Find content across <span>Bitcoin Live</span>
              </SearchHeroTitle>
              <SearchHeroSubtitle>
                Search posts, alerts, tags, and topics from our analysts.
              </SearchHeroSubtitle>
            </>
          )}

          <SearchHeroFormWrap>
            <SiteSearchBar
              variant="hero"
              autoFocus={!hasQuery}
              placeholder="Search…"
            />
          </SearchHeroFormWrap>
        </SearchHeroInner>
      </SearchHero>

      <SearchFeedSection>
        <SearchFeedInner>
          {!hasQuery && (
            <SearchStateCard>
              <SearchStateText>
                Enter a keyword to search posts, alerts, tags, and topics.
              </SearchStateText>
            </SearchStateCard>
          )}

          {hasQuery && loading && <SearchFeedSkeleton />}

          {hasQuery && !loading && total === 0 && (
            <>
              {resultsSummary}
              <SearchStateCard>
                <SearchStateTitle>No results found</SearchStateTitle>
                <SearchStateText>
                  Try different keywords or check your spelling
                </SearchStateText>
              </SearchStateCard>
            </>
          )}

          {hasQuery && !loading && total > 0 && (
            <>
              {resultsSummary}
              <SearchResultList>
                {posts.map((post) => (
                  <SearchResultCard key={post.id} post={post} />
                ))}
              </SearchResultList>

              {totalPages > 1 && (
                <SearchPaginationWrap>
                  <MembersPagination
                    activePage={activePage}
                    totalPages={totalPages}
                    onChange={handlePageChange}
                  />
                </SearchPaginationWrap>
              )}
            </>
          )}
        </SearchFeedInner>
      </SearchFeedSection>

      <Footer onSubscribeClick={goToPricing} hideSubscribeCta={loggedIn} />
    </SearchPage>
  );
};

export default Search;
